/**
 * Transcript Generator Utility
 *
 * Generates Markdown transcripts from episode evaluation conversations.
 * Handles conversation history, tool calls, and optional Judge results.
 *
 * Story: 24.2 - Evolve Single Episode Eval
 */

import type {
  EpisodeTestingData,
  ConversationMessage,
  ToolCallRecord,
  EvaluateConversationResponse,
} from '../services/testingApi';

const CEFR_MAP: Record<number, string> = {
  1: 'A1',
  2: 'A2',
  3: 'B1',
  4: 'B2',
  5: 'C1',
};

function formatToolCall(tc: ToolCallRecord, goals: string[]): string {
  if (tc.toolName === 'goal_completed') {
    // Arguments keys are camelCase after transformSnakeToCamel (goalText, goalIndex)
    // but also check snake_case in case raw data is passed
    const args = tc.arguments ?? {};
    let goalText = (args.goalText ?? args.goal_text ?? '') as string;
    if (!goalText) {
      const idx = (args.goalIndex ?? args.goal_index ?? 0) as number;
      goalText = idx >= 1 && idx <= goals.length ? goals[idx - 1] : idx > 0 ? String(idx) : '';
    }
    return `🎯 goal_completed: "${goalText}"`;
  }
  if (tc.toolName === 'scenario_exhausted') {
    return '🏁 scenario_exhausted';
  }
  const args = tc.arguments ? JSON.stringify(tc.arguments) : '';
  return args ? `🔧 ${tc.toolName}(${args})` : `🔧 ${tc.toolName}`;
}

interface TranscriptOptions {
  episode: EpisodeTestingData;
  conversationHistory: ConversationMessage[];
  toolCalls: ToolCallRecord[];
  evaluationResults?: EvaluateConversationResponse | null;
  completionReason: string;
  exchangeCount: number;
  personaName?: string;
}

export function generateTranscript(options: TranscriptOptions): string {
  const {
    episode,
    conversationHistory,
    toolCalls,
    evaluationResults,
    completionReason,
    exchangeCount,
    personaName,
  } = options;
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const cefrCode = CEFR_MAP[episode.complexityLevel] || `L${episode.complexityLevel}`;

  const lines: string[] = [];

  // Header
  lines.push('# Episode Eval Transcript');
  lines.push(`- Episode: ${episode.name}`);
  lines.push(`- Language: ${episode.language}`);
  lines.push(`- Complexity: ${cefrCode}`);
  if (personaName) {
    lines.push(`- Persona: ${personaName}`);
  }
  lines.push(`- Date: ${timestamp}`);
  lines.push(`- Exchanges: ${exchangeCount}`);
  const lastToolCall = toolCalls.filter(
    (tc) => tc.toolName === 'scenario_exhausted' || tc.toolName === 'end_episode_chat'
  );
  const completionLabel = lastToolCall.length > 0 ? lastToolCall[0].toolName : completionReason;
  lines.push(`- Completion: ${completionLabel}`);
  lines.push('');

  // Episode metadata
  lines.push('## Episode Details');
  lines.push('');
  lines.push('### Context');
  lines.push(episode.context);
  lines.push('');
  lines.push('### Scenario');
  lines.push(episode.scenario);
  lines.push('');
  lines.push('### Goals');
  episode.goals.forEach((goal, i) => {
    lines.push(`${i + 1}. ${goal}`);
  });
  lines.push('');
  lines.push('### Personas');
  episode.personas.forEach((p) => {
    lines.push(`- **${p.name}**: ${p.description}`);
  });
  lines.push('');

  // Conversation (with tool calls interleaved)
  // Group tool calls by afterTurn
  const toolCallsByTurn: Record<number, ToolCallRecord[]> = {};
  for (const tc of toolCalls) {
    const turn = tc.afterTurn;
    if (!toolCallsByTurn[turn]) toolCallsByTurn[turn] = [];
    toolCallsByTurn[turn].push(tc);
  }

  lines.push('## Conversation');
  let turn = 0;
  for (const msg of conversationHistory) {
    // Try to parse as JSON turns (multi-persona format) for both roles
    try {
      const turns = JSON.parse(msg.content);
      if (Array.isArray(turns)) {
        for (const t of turns) {
          const label = msg.role === 'user' ? 'USER' : (t.persona || 'ASSISTANT').toUpperCase();
          lines.push(`${label}: ${t.message}`);
        }
      } else {
        lines.push(`${msg.role === 'user' ? 'USER' : 'ASSISTANT'}: ${msg.content}`);
      }
    } catch {
      lines.push(`${msg.role === 'user' ? 'USER' : 'ASSISTANT'}: ${msg.content}`);
    }

    // Insert tool calls after each assistant message
    if (msg.role === 'assistant') {
      if (toolCallsByTurn[turn]) {
        for (const tc of toolCallsByTurn[turn]) {
          lines.push(`> ${formatToolCall(tc, episode.goals)}`);
        }
      }
      turn++;
    }
  }
  lines.push('');

  // Judge Evaluation (optional)
  if (evaluationResults) {
    lines.push('## Judge Evaluation');
    lines.push(`- Overall Score: ${(evaluationResults.overallScore * 100).toFixed(0)}%`);
    lines.push(`- Decision: ${evaluationResults.decision}`);
    lines.push('');

    lines.push('### Criteria Scores');
    lines.push('| Criteria | Score | Evidence |');
    lines.push('|----------|-------|----------|');
    for (const [key, cs] of Object.entries(evaluationResults.criteriaScores)) {
      const evidence = cs.evidence.length > 0 ? cs.evidence.map((e) => `"${e}"`).join('; ') : '-';
      lines.push(`| ${key} | ${(cs.score * 100).toFixed(0)}% | ${evidence} |`);
    }
    lines.push('');

    lines.push('### Violations');
    if (evaluationResults.disallowedBehaviorViolations.length === 0) {
      lines.push('No violations detected.');
    } else {
      lines.push('| Rule | Evidence |');
      lines.push('|------|----------|');
      for (const v of evaluationResults.disallowedBehaviorViolations) {
        lines.push(`| ${v.rule} | "${v.evidence}" |`);
      }
    }
    lines.push('');

    if (evaluationResults.recommendations.length > 0) {
      lines.push('### Recommendations');
      for (const rec of evaluationResults.recommendations) {
        lines.push(`- ${rec}`);
      }
      lines.push('');
    }

    if (evaluationResults.strengths.length > 0) {
      lines.push('### Strengths');
      for (const s of evaluationResults.strengths) {
        lines.push(`- ${s}`);
      }
      lines.push('');
    }
  }

  return lines.join('\n');
}

export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function downloadTranscript(options: TranscriptOptions): void {
  const content = generateTranscript(options);
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const slug = slugify(options.episode.name);
  const fileName = `eval-${slug}-${timestamp}.md`;

  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
